"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import ChevronDownIcon from "../icons/ChevronDownIcon";
import ChevronUpIcon from "../icons/ChevronUpIcon";
import SponsoredBanners from "../ui/SponsoredBanners";
import { Button } from "@/components";
import SelectDeliveryMenuSection from "./SelectDeliveryMenuSection";

// ─── Button style constants ───────────────────────────────────────────────────

const TAB_BTN_BASE =
  "flex w-full items-center justify-center rounded-xl px-5 py-3 text-body-md font-medium transition-colors transform-none active:!scale-100";
const TAB_BTN_ACTIVE = "bg-blue-80 text-constant-white";
const TAB_BTN_INACTIVE =
  "bg-constant-white text-text-secondary";

const HUB_TOPIC_ACTIVE = "border-blue-80 !bg-blue-80 text-constant-white";
const HUB_TOPIC_INACTIVE =
  "border-neutral-10 bg-constant-white text-text-secondary";
const HUB_MOBILE_REMAINING =
  "border-neutral-10 bg-blue-10 text-text-secondary";

const FAQ_ACCORDION_BTN =
  "flex w-full items-center justify-between gap-3 px-5 py-4 text-left shadow-none transition-shadow hover:!shadow-none focus:!shadow-none active:!shadow-none sm:px-6";

import {
  POLICY_CANCELLATION_SNIPPET,
  POLICY_DELIVERY_WINDOW_SNIPPET,
  POLICY_MAIL_ORDER_TRANSIT_SNIPPET,
  POLICY_REFUND_PROCESS_SNIPPET,
  POLICY_SUBSTITUTION_SNIPPET,
} from "@/lib/policy-snippets";

type CarouselImage = {
  desktopUri: string;
  mobileUri?: string;
  order: number;
};

type SponsoredBanner = {
  id: string;
  title: string;
  bannerImageUri: string | null;
  desktopImageUri: string | null;
  mobileImageUri: string | null;
  bannerImages: CarouselImage[] | null;
  linkType: 'product' | 'collection' | 'brand' | 'custom';
  linkTarget: string | null;
  customUrl: string | null;
  position: string | null;
  order: number;
  hasCarousel?: boolean;
  desktopRatio?: string;
  mobileRatio?: string;
};

type FAQ = {
  id: number;
  question: string;
  answer: string;
  category: FAQCategoryId;
};

type FAQCategoryId = "general" | "same-day" | "mom";

const categories: Array<{ id: FAQCategoryId; label: string }> = [
  { id: "general", label: "General" },
  { id: "same-day", label: "Same-day delivery" },
  { id: "mom", label: "MOM delivery" },
];

type FAQSectionProps = {
  banners?: SponsoredBanner[]; // Optional: pre-fetched banners
  /** Sponsored banner slot position (CMS). Default "4". */
  sponsoredPosition?: string;
  /** Hide the "View All FAQs" link, use when already on the /faq page. */
  hideViewAll?: boolean;
  /** Toggle sponsored banners under FAQ section. */
  showSponsored?: boolean;
};

const faqs: FAQ[] = [
  {
    id: 1,
    question: "What valid ID is required to purchase cannabis online?",
    answer: "You must be 18+ and provide valid government-issued photo ID such as a driver's license, passport, or Alberta ID card at the time of delivery or pickup.",
    category: "general",
  },
  {
    id: 2,
    question: "Can I browse the menu before creating an account?",
    answer: "Yes. You can explore our cannabis menu, compare products, and review pricing before signing in. You'll only need to complete account verification when you're ready to place an order.",
    category: "general",
  },
  {
    id: 3,
    question: "What payment methods do you accept?",
    answer: "We support secure online payments for most orders, and certain fulfillment options may also allow in-store or on-delivery payment depending on your location and selected service.",
    category: "general",
  },
  {
    id: 4,
    question: "How do I track my cannabis order after checkout?",
    answer: "Once your order is confirmed, we'll send live status updates by text and email when available. Delivery orders also receive tracking details so you know exactly when to expect your package.",
    category: "general",
  },
  {
    id: 5,
    question: "How fast is same-day weed delivery in Calgary?",
    answer: "We aim to deliver your order in as little as 45 minutes after receiving it. You will receive text updates and live tracking once our driver is on route, ensuring your favourite cannabis products arrive quickly and discreetly",
    category: "same-day",
  },
  {
    id: 6,
    question: "What is the minimum order amount for same-day weed delivery in Calgary?",
    answer: "There is no minimum order amount for same-day weed delivery in Calgary. You can order as little as you need.",
    category: "same-day",
  },
  {
    id: 7,
    question: "Can I choose a delivery window for same-day orders?",
    answer: POLICY_DELIVERY_WINDOW_SNIPPET,
    category: "same-day",
  },
  {
    id: 8,
    question: "Will I get updates while my same-day order is on the way?",
    answer: "Absolutely. We send order progress updates and, when supported, live tracking details so you know when your driver is on route and when to be ready with valid ID.",
    category: "same-day",
  },
  {
    id: 9,
    question: "Do you offer mail-order marijuana shipping across Alberta?",
    answer: "Yes, we provide secure mail-order marijuana shipping across Alberta. Orders are packaged discreetly and shipped via Canada Post with tracking.",
    category: "mom",
  },
  {
    id: 10,
    question: "How long does mail-order delivery usually take?",
    answer: POLICY_MAIL_ORDER_TRANSIT_SNIPPET,
    category: "mom",
  },
  {
    id: 11,
    question: "Are mail-order cannabis packages discreet?",
    answer: "Yes. All MOM orders are packed discreetly with privacy in mind. Packages do not display product details externally, and tracking is provided after shipment.",
    category: "mom",
  },
  {
    id: 12,
    question: "Is there a shipping minimum for Alberta mail orders?",
    answer: "Yes. Free shipping thresholds and order minimums vary by promotion, but standard MOM orders must meet the required subtotal shown during checkout before shipment is processed.",
    category: "mom",
  },
];

type FAQHubTopic = {
  id: string;
  label: string;
};

type FAQHubItem = {
  id: string;
  question: string;
  answer: string;
  topicId: string;
};

type FAQHubBlockProps = {
  title: string;
  topics: FAQHubTopic[];
  items: FAQHubItem[];
};

function FAQHubBlock({ title, topics, items }: FAQHubBlockProps) {
  const [activeTopic, setActiveTopic] = useState<string>(topics[0]?.id ?? "");
  const visibleItems = useMemo(
    () => items.filter((item) => item.topicId === activeTopic),
    [items, activeTopic]
  );
  const [openId, setOpenId] = useState<string | null>(visibleItems[0]?.id ?? null);

  useEffect(() => {
    setOpenId(visibleItems[0]?.id ?? null);
  }, [visibleItems]);

  const activeTopicObj = topics.find((topic) => topic.id === activeTopic) ?? topics[0];
  const remainingTopics = topics.filter((topic) => topic.id !== activeTopic);

  return (
    <section className="w-full" aria-label={title}>
      <h2 className="text-h3-mobile font-bold text-text-primary sm:text-h3-desktop">{title}</h2>
      <div className="grid gap-5 lg:grid-cols-[360px_1fr] lg:gap-10">
        <div className="space-y-3 lg:space-y-3 mt-2 lg:mt-3">
          {/* Desktop topic list */}
          <div className="hidden overflow-x-auto pb-1 lg:block lg:overflow-visible lg:pb-0">
            <div className="flex min-w-max gap-2 lg:block lg:min-w-0">
              {topics.map((topic, index) => {
                const isActive = topic.id === activeTopic;
                return (
                  <Button
                    key={topic.id}
                    variant="bordered"
                    size="lg"
                    onClick={() => setActiveTopic(topic.id)}
                    className={[
                      "lg:w-full",
                      isActive ? HUB_TOPIC_ACTIVE : HUB_TOPIC_INACTIVE,
                      index < topics.length - 1 ? "lg:mb-1" : "",
                      "rounded-xl"
                    ].filter(Boolean).join(" ")}
                  >
                    {topic.label}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {/* Mobile: active topic shown first */}
          {activeTopicObj ? (
            <Button
              variant="ghost"
              size="lg"
              onClick={() => setActiveTopic(activeTopicObj.id)}
              className={`${HUB_TOPIC_ACTIVE} w-full lg:hidden`}
            >
              {activeTopicObj.label}
            </Button>
          ) : null}

          {visibleItems.map((item) => {
            const expanded = openId === item.id;
            return (
              <div
                key={item.id}
                className="overflow-hidden rounded-2xl border border-neutral-10 bg-constant-white shadow-sm"
              >
                <Button
                  raw
                  type="button"
                  onClick={() => setOpenId((prev) => (prev === item.id ? null : item.id))}
                  aria-expanded={expanded}
                  aria-controls={`${item.id}-answer`}
                  className={`group ${FAQ_ACCORDION_BTN}`}
                >
                  <span className="text-body-18 sm:text-xl font-medium text-text-primary">
                    {item.question}
                  </span>
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-neutral-10 bg-constant-white text-blue-80">
                    {expanded ? (
                      <ChevronUpIcon size={16} className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon size={16} className="h-4 w-4" />
                    )}
                  </span>
                </Button>
                <div
                  id={`${item.id}-answer`}
                  className={`overflow-hidden border-t border-neutral-10 px-5 transition-all duration-300 sm:px-6 ${expanded ? "max-h-[360px] py-4 opacity-100" : "max-h-0 py-0 opacity-0"
                    }`}
                >
                  <p className="text-body-16 sm:text-body-18 font-medium leading-relaxed text-text-secondary">{item.answer}</p>
                </div>
              </div>
            );
          })}

          {/* Mobile: remaining topics appear after FAQ cards */}
          <div className="space-y-2 pt-1 lg:hidden">
            {remainingTopics.map((topic) => (
              <Button
                key={topic.id}
                variant="tertiary"
                size="lg"
                onClick={() => setActiveTopic(topic.id)}
                className={`${HUB_MOBILE_REMAINING} w-full`}
              >
                {topic.label}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const GENERAL_TOPICS: FAQHubTopic[] = [
  { id: "legal", label: "Legal & compliance" },
  { id: "purchase", label: "Purchasing & ordering" },
  { id: "returns", label: "Returns & loyalty program" },
  { id: "quality", label: "Product info & quality" },
];

const GENERAL_ITEMS: FAQHubItem[] = [
  {
    id: "general-1",
    topicId: "legal",
    question: "What valid ID is required to purchase cannabis online?",
    answer:
      "You must be 18+ and show valid government-issued photo ID such as a driver's license, passport, or Alberta ID card at pickup or delivery.",
  },
  {
    id: "general-2",
    topicId: "legal",
    question: "Is cannabis delivery legal in Alberta?",
    answer:
      "Yes. Delivery is legal when fulfilled by licensed Alberta retailers and completed with age verification at handoff.",
  },
  {
    id: "general-3",
    topicId: "legal",
    question: "Can someone else receive my order for me?",
    answer:
      "No. The registered customer must be present with valid photo ID to receive the order.",
  },
  {
    id: "general-4",
    topicId: "legal",
    question: "Do you serve all Alberta cities for every order type?",
    answer:
      "Same-day service depends on your area. Mail-order is available to Alberta postal codes where Canada Post service is active.",
  },
  {
    id: "general-5",
    topicId: "purchase",
    question: "Can I browse products before creating an account?",
    answer:
      "Yes. You can explore the full menu, compare products, and review pricing before signing in. Account verification is required only at checkout.",
  },
  {
    id: "general-6",
    topicId: "purchase",
    question: "What payment methods do you accept?",
    answer:
      "We support secure online payment options and may offer additional methods depending on your selected service.",
  },
  {
    id: "general-7",
    topicId: "purchase",
    question: "Is there a minimum order amount?",
    answer:
      "Minimums depend on service type and promotions. Any minimum or threshold appears clearly during checkout.",
  },
  {
    id: "general-8",
    topicId: "purchase",
    question: "Can I edit or cancel my order after checkout?",
    answer:
      "If fulfillment has not started, support can usually help adjust or cancel. Once packed or shipped, changes may be limited.",
  },
  {
    id: "general-9",
    topicId: "returns",
    question: "What items are eligible for return?",
    answer:
      "Defective hardware may be eligible if returned with receipt and packaging within policy windows. Unopened product returns vary by product type.",
  },
  {
    id: "general-10",
    topicId: "returns",
    question: "Are accessories final sale?",
    answer:
      "Most accessories are final sale unless there is a confirmed manufacturer defect.",
  },
  {
    id: "general-11",
    topicId: "returns",
    question: "How does your loyalty program work?",
    answer:
      "Eligible purchases earn points that can be redeemed on future orders. Point values and exclusions are shown in your account.",
  },
  {
    id: "general-12",
    topicId: "returns",
    question: "Can I combine loyalty rewards with promos?",
    answer:
      "Some promos can be stacked while others cannot. Checkout displays which discounts can be combined.",
  },
  {
    id: "general-13",
    topicId: "quality",
    question: "How do I compare THC, CBD, and terpene details?",
    answer:
      "Each product page includes potency and strain information where provided by the licensed producer.",
  },
  {
    id: "general-14",
    topicId: "quality",
    question: "Do you carry indica, sativa, and hybrid options?",
    answer:
      "Yes. You can filter by category and effects to quickly browse flower, pre-rolls, vapes, edibles, and more.",
  },
  {
    id: "general-15",
    topicId: "quality",
    question: "How often is menu inventory updated?",
    answer:
      "Inventory updates throughout the day as products sell and restock so availability stays accurate.",
  },
  {
    id: "general-16",
    topicId: "quality",
    question: "What if a product is out of stock after I order?",
    answer: POLICY_SUBSTITUTION_SNIPPET,
  },
];

const SAME_DAY_TOPICS: FAQHubTopic[] = [
  { id: "tracking", label: "Delivery process & tracking" },
  { id: "areas", label: "Delivery areas & timing" },
  { id: "id", label: "Identification & age verification" },
  { id: "payment", label: "Payment & charges" },
  { id: "selection", label: "Product selection & availability" },
  { id: "refunds", label: "Tips, refunds, & cancellations" },
  { id: "legal", label: "Legal & compliance" },
  { id: "loyalty", label: "Loyalty & promotions" },
];

const SAME_DAY_ITEMS: FAQHubItem[] = [
  {
    id: "same-day-1",
    topicId: "tracking",
    question: "Can I track my delivery?",
    answer:
      "Yes. Once a driver is assigned, you'll receive real-time updates and tracking details by text or email when available.",
  },
  {
    id: "same-day-2",
    topicId: "tracking",
    question: "What if I'm not home to receive the delivery?",
    answer:
      "If you are unavailable, our team will attempt to contact you. Orders cannot be left unattended and require ID verification at handoff.",
  },
  {
    id: "same-day-3",
    topicId: "tracking",
    question: "Is curbside delivery allowed?",
    answer:
      "Yes, when local conditions permit. You must present valid government-issued photo ID directly to the driver.",
  },
  {
    id: "same-day-4",
    topicId: "tracking",
    question: "Will I get ETA updates before arrival?",
    answer:
      "Yes. You'll receive status notifications as your order moves from confirmed to out-for-delivery.",
  },
  {
    id: "same-day-5",
    topicId: "areas",
    question: "Which areas are eligible for same-day delivery?",
    answer:
      "Coverage includes Calgary and nearby service zones. Enter your address or postal code to confirm availability in real time.",
  },
  {
    id: "same-day-6",
    topicId: "areas",
    question: "How fast can same-day orders arrive?",
    answer:
      "Many orders arrive in as little as 45 minutes depending on demand, address, and weather conditions.",
  },
  {
    id: "same-day-7",
    topicId: "areas",
    question: "Can I pick a preferred delivery window?",
    answer: POLICY_DELIVERY_WINDOW_SNIPPET,
  },
  {
    id: "same-day-8",
    topicId: "areas",
    question: "Do you deliver late evenings?",
    answer:
      "Delivery hours vary by city and regulations. The checkout flow always shows the next available windows.",
  },
  {
    id: "same-day-9",
    topicId: "id",
    question: "Do I need ID every time for same-day delivery?",
    answer:
      "Yes. Drivers verify government-issued photo ID at each handoff to comply with Alberta regulations.",
  },
  {
    id: "same-day-10",
    topicId: "id",
    question: "Can I use a digital photo of my ID?",
    answer:
      "No. A physical, valid, government-issued photo ID must be shown at delivery.",
  },
  {
    id: "same-day-11",
    topicId: "id",
    question: "Can another adult at my address accept delivery?",
    answer:
      "Only if they are the verified account holder for the order and can present valid ID.",
  },
  {
    id: "same-day-12",
    topicId: "id",
    question: "What happens if ID does not match the order?",
    answer:
      "The order cannot be completed and support will contact you with next steps.",
  },
  {
    id: "same-day-13",
    topicId: "payment",
    question: "When am I charged for same-day orders?",
    answer:
      "Payment timing depends on method selected at checkout, but totals are confirmed before dispatch.",
  },
  {
    id: "same-day-14",
    topicId: "payment",
    question: "Are there delivery fees?",
    answer:
      "Fees may apply based on location and basket size. Any fee appears clearly before order confirmation.",
  },
  {
    id: "same-day-15",
    topicId: "payment",
    question: "Can I apply promo codes to same-day orders?",
    answer:
      "Yes, eligible promo codes can be applied during checkout before placing your order.",
  },
  {
    id: "same-day-16",
    topicId: "payment",
    question: "Can I tip the driver?",
    answer:
      "Tip options may be available at checkout depending on payment method and delivery flow.",
  },
  {
    id: "same-day-17",
    topicId: "selection",
    question: "Is the same-day menu different from in-store stock?",
    answer:
      "Some products overlap, but the same-day menu can differ by fulfillment location and live inventory.",
  },
  {
    id: "same-day-18",
    topicId: "selection",
    question: "Can I order from multiple categories in one delivery?",
    answer:
      "Absolutely. You can mix flower, edibles, pre-rolls, vapes, and accessories in one order.",
  },
  {
    id: "same-day-19",
    topicId: "selection",
    question: "What if an item sells out after checkout?",
    answer: POLICY_SUBSTITUTION_SNIPPET,
  },
  {
    id: "same-day-20",
    topicId: "selection",
    question: "Do product limits apply on same-day orders?",
    answer:
      "Legal possession limits and compliance rules apply to all cannabis purchases.",
  },
  {
    id: "same-day-21",
    topicId: "refunds",
    question: "Can I cancel a same-day order?",
    answer: POLICY_CANCELLATION_SNIPPET,
  },
  {
    id: "same-day-22",
    topicId: "refunds",
    question: "What if my product has an issue?",
    answer:
      "Contact support with your order number and product details so we can review return or exchange options.",
  },
  {
    id: "same-day-23",
    topicId: "refunds",
    question: "How are refunds processed?",
    answer: POLICY_REFUND_PROCESS_SNIPPET,
  },
  {
    id: "same-day-24",
    topicId: "refunds",
    question: "Can I get support after delivery?",
    answer:
      "Yes. Our support team can help with order follow-up, product concerns, and account questions.",
  },
  {
    id: "same-day-25",
    topicId: "legal",
    question: "Is same-day delivery AGLC compliant?",
    answer:
      "Yes. Orders are fulfilled by licensed retailers and completed with mandatory age verification.",
  },
  {
    id: "same-day-26",
    topicId: "legal",
    question: "Can cannabis be delivered to public places?",
    answer:
      "No. Deliveries must be completed at valid addresses where handoff and ID verification can occur safely.",
  },
  {
    id: "same-day-27",
    topicId: "legal",
    question: "Can I place an order for someone else?",
    answer:
      "Orders must be placed by and delivered to the verified adult purchaser.",
  },
  {
    id: "same-day-28",
    topicId: "legal",
    question: "Are there quantity restrictions per order?",
    answer:
      "Yes. Alberta legal limits apply and are enforced during checkout.",
  },
  {
    id: "same-day-29",
    topicId: "loyalty",
    question: "Do same-day orders earn loyalty points?",
    answer:
      "Yes, eligible same-day purchases can earn points in your account.",
  },
  {
    id: "same-day-30",
    topicId: "loyalty",
    question: "Can I redeem loyalty points on delivery orders?",
    answer:
      "Yes, when rewards are available and applicable to the items in your cart.",
  },
  {
    id: "same-day-31",
    topicId: "loyalty",
    question: "Do promotions apply to same-day carts?",
    answer:
      "Many promos apply automatically or via code depending on campaign rules.",
  },
  {
    id: "same-day-32",
    topicId: "loyalty",
    question: "Where can I see current offers?",
    answer:
      "Visit the deals area in the menu and your account dashboard for active offers and points status.",
  },
];

const MOM_TOPICS: FAQHubTopic[] = [
  { id: "general", label: "General mail order" },
  { id: "requirements", label: "Order requirements & payment" },
  { id: "shipping", label: "Shipping & delivery" },
  { id: "receiving", label: "Receiving & identification" },
  { id: "packaging", label: "Packaging & privacy" },
  { id: "products", label: "Products & loyalty program" },
  { id: "support", label: "Returns & customer support" },
];

const MOM_ITEMS: FAQHubItem[] = [
  {
    id: "mom-1",
    topicId: "general",
    question: "Will my order be delayed due to the Canada Post service disruption?",
    answer:
      "Transit timelines may vary during service disruptions. We always provide tracking so you can monitor updates and expected delivery windows.",
  },
  {
    id: "mom-2",
    topicId: "general",
    question: "Do you offer mail-order marijuana services?",
    answer:
      "Yes. We ship legal cannabis products across Alberta through our secure mail-order program with discreet packaging.",
  },
  {
    id: "mom-3",
    topicId: "general",
    question: "Is weed delivery legal in Alberta?",
    answer:
      "Yes. Cannabis delivery and mail-order services are legal when fulfilled by licensed retailers and age verification requirements are met.",
  },
  {
    id: "mom-4",
    topicId: "general",
    question: "Are there any legal risks with using an unlicensed mail-order service?",
    answer:
      "Unlicensed services can expose customers to legal and product safety risks. Ordering through licensed retailers protects quality and compliance.",
  },
  {
    id: "mom-5",
    topicId: "requirements",
    question: "Is there a minimum subtotal for mail-order?",
    answer:
      "Minimum order thresholds may apply and are clearly shown at checkout before payment.",
  },
  {
    id: "mom-6",
    topicId: "requirements",
    question: "What payment methods are available for mail-order?",
    answer:
      "Payment options are displayed at checkout and may vary based on account status and promotion rules.",
  },
  {
    id: "mom-7",
    topicId: "requirements",
    question: "Can I apply promo codes to mail-order carts?",
    answer:
      "Yes, eligible promo codes can be added during checkout prior to placing your order.",
  },
  {
    id: "mom-8",
    topicId: "requirements",
    question: "Can I change items after placing a mail-order?",
    answer:
      "Changes are sometimes possible before fulfillment begins. Contact support as soon as possible.",
  },
  {
    id: "mom-9",
    topicId: "shipping",
    question: "How long does mail-order shipping usually take?",
    answer: POLICY_MAIL_ORDER_TRANSIT_SNIPPET,
  },
  {
    id: "mom-10",
    topicId: "shipping",
    question: "Do I receive a tracking number?",
    answer:
      "Yes. Tracking details are sent once your package is handed to the carrier.",
  },
  {
    id: "mom-11",
    topicId: "shipping",
    question: "Do you offer free shipping promotions?",
    answer:
      "Free shipping promos may be available based on basket size or campaign windows.",
  },
  {
    id: "mom-12",
    topicId: "shipping",
    question: "What happens during carrier delays?",
    answer:
      "Carrier delays are monitored closely and support can help with tracking follow-up when needed.",
  },
  {
    id: "mom-13",
    topicId: "receiving",
    question: "Do I need ID when receiving a package?",
    answer:
      "Yes. Valid government-issued ID is required to verify age for cannabis shipments.",
  },
  {
    id: "mom-14",
    topicId: "receiving",
    question: "Can someone else sign for my package?",
    answer:
      "The receiver must satisfy legal age and identification requirements; carrier policies determine acceptance specifics.",
  },
  {
    id: "mom-15",
    topicId: "receiving",
    question: "Can packages be delivered to PO boxes?",
    answer:
      "Eligibility depends on carrier rules and service availability for your destination.",
  },
  {
    id: "mom-16",
    topicId: "receiving",
    question: "What if I miss the delivery attempt?",
    answer:
      "Canada Post will provide next-step instructions for pickup or re-delivery when available.",
  },
  {
    id: "mom-17",
    topicId: "packaging",
    question: "Are mail-order packages discreet?",
    answer:
      "Yes. Packaging is plain and privacy-focused without visible product details on the exterior.",
  },
  {
    id: "mom-18",
    topicId: "packaging",
    question: "Is smell-proof packaging used?",
    answer:
      "Orders are packed securely using sealed methods designed to protect product integrity during transit.",
  },
  {
    id: "mom-19",
    topicId: "packaging",
    question: "Will the package show that it contains cannabis?",
    answer:
      "No explicit product naming is displayed externally; labeling follows legal shipping requirements.",
  },
  {
    id: "mom-20",
    topicId: "packaging",
    question: "How is product freshness protected in transit?",
    answer:
      "Products remain in original compliant packaging and are packed to reduce movement during shipping.",
  },
  {
    id: "mom-21",
    topicId: "products",
    question: "Is the mail-order menu different from same-day?",
    answer:
      "Selection can vary by fulfillment channel, but both menus are updated regularly with new drops and staples.",
  },
  {
    id: "mom-22",
    topicId: "products",
    question: "Can I buy accessories through mail-order?",
    answer:
      "Yes, eligible accessories can be added to mail-order carts when available.",
  },
  {
    id: "mom-23",
    topicId: "products",
    question: "Do mail-order purchases earn loyalty points?",
    answer:
      "Eligible mail-order purchases contribute to loyalty points based on current program rules.",
  },
  {
    id: "mom-24",
    topicId: "products",
    question: "How can I find best-value products quickly?",
    answer:
      "Use sorting and filters for potency, format, and price to surface high-value options fast.",
  },
  {
    id: "mom-25",
    topicId: "support",
    question: "What if my package arrives damaged?",
    answer:
      "Contact support right away with photos and order details so we can assess and resolve quickly.",
  },
  {
    id: "mom-26",
    topicId: "support",
    question: "How do I request a return review?",
    answer:
      "Submit your request with receipt and product details through support channels listed in your order confirmation.",
  },
  {
    id: "mom-27",
    topicId: "support",
    question: "How fast does customer support respond?",
    answer:
      "Response times vary by volume, but we prioritize active-order and delivery-related requests.",
  },
  {
    id: "mom-28",
    topicId: "support",
    question: "Can support help with tracking updates?",
    answer:
      "Yes. Share your order number and tracking reference and our team will assist with carrier follow-up.",
  },
];

export function FAQHubSections() {
  return (
    <div className="w-full space-y-12 sm:space-y-16">
      <FAQHubBlock title="General questions" topics={GENERAL_TOPICS} items={GENERAL_ITEMS} />
      <FAQHubBlock title="Same-day questions" topics={SAME_DAY_TOPICS} items={SAME_DAY_ITEMS} />
      <FAQHubBlock title="Mail-order questions" topics={MOM_TOPICS} items={MOM_ITEMS} />
      <SelectDeliveryMenuSection />
    </div>
  );
}

export default function FAQSection({
  banners,
  sponsoredPosition,
  hideViewAll = false,
  showSponsored = true,
}: FAQSectionProps = {}) {
  const [activeCategory, setActiveCategory] = useState<FAQCategoryId>("general");
  const filteredFaqs = useMemo(
    () => faqs.filter((faq) => faq.category === activeCategory),
    [activeCategory]
  );
  const [openId, setOpenId] = useState<number | null>(filteredFaqs[0]?.id ?? null);

  useEffect(() => {
    setOpenId(filteredFaqs[0]?.id ?? null);
  }, [filteredFaqs]);

  const toggleFAQ = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section className="w-full px-4 sm:px-6 lg:px-8" aria-label="Frequently Asked Questions">
      <div className="w-full flex flex-col lg:flex-row gap-6 lg:gap-10">
        {/* Left Side - FAQ Header */}
        <div className="lg:w-[40%] flex flex-col gap-5">
          <div>
            <h2 className="text-h2-mobile sm:text-h2-desktop font-bold text-text-primary leading-[0.95]">
              Got questions? We have answers for you
            </h2>
          </div>

          <div className="overflow-hidden rounded-2xl border border-neutral-10 bg-constant-white p-1">
            {categories.map((category, index) => {
              const isActive = activeCategory === category.id;
              return (
                <Button
                  key={category.id}
                  variant="tertiary"
                  size="lg"
                  onClick={() => setActiveCategory(category.id)}
                  className={[
                    TAB_BTN_BASE,
                    isActive ? TAB_BTN_ACTIVE : TAB_BTN_INACTIVE,
                    index < categories.length - 1 ? "mb-1" : "",
                  ].filter(Boolean).join(" ")}
                >
                  {category.label}
                </Button>
              );
            })}
          </div>

          {!hideViewAll && (
            <Button asChild variant="primary" size="lg" className="px-0 rounded-xl">
              <Link href="/faq">
                View All FAQs
              </Link>
            </Button>
          )}
        </div>

        {/* Right Side - FAQ Accordion */}
        <div className="lg:w-[60%] flex flex-col gap-3">
          {filteredFaqs.map((faq) => {
            const isExpanded = openId === faq.id;

            return (
              <div
                key={faq.id}
                className="overflow-hidden rounded-2xl border border-neutral-10 bg-constant-white shadow-sm transition-all duration-300 ease-in-out"
              >
                {/* Question row + icon toggle */}
                <div className="flex w-full items-start justify-between gap-3 bg-constant-white px-5 py-4 sm:px-6">
                  <h3
                    id={`faq-heading-${faq.id}`}
                    className="min-w-0 flex-1 text-body-20 font-medium text-text-primary"
                  >
                    {faq.question}
                  </h3>
                  <Button
                    type="button"
                    variant="icon"
                    color="white"
                    size="icon-md"
                    aria-expanded={isExpanded}
                    aria-controls={`faq-panel-${faq.id}`}
                    aria-label={isExpanded ? `Collapse answer: ${faq.question}` : `Expand answer: ${faq.question}`}
                    onClick={() => toggleFAQ(faq.id)}
                  >
                    {isExpanded
                      ? <ChevronUpIcon size={16} color="currentColor" />
                      : <ChevronDownIcon size={16} color="currentColor" />
                    }
                  </Button>
                </div>

                {/* Answer Content */}
                <div
                  id={`faq-panel-${faq.id}`}
                  role="region"
                  aria-labelledby={`faq-heading-${faq.id}`}
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-[1000px] border-t border-neutral-10 px-5 py-4 opacity-100 sm:px-6' : 'max-h-0 opacity-0'
                    }`}
                >
                  <p className="text-body-md font-medium leading-relaxed text-text-secondary">
                    {faq.answer}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* No slot named, no banner. This used to default to the homepage's
          position 4, so every page reusing the FAQ block inherited the
          homepage's banner whether or not its admin page declared one. */}
      {showSponsored && sponsoredPosition ? (
        <div className="sm:mt-8 mt-4">
          <SponsoredBanners className="w-full" position={sponsoredPosition} />
        </div>
      ) : null}
    </section>
  );
}
